import { useContext, useEffect, useRef } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Editor } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { MinimumBoundingBox } from '@editor/services/MinimumBoundingBox'
import { transform2Dto3D } from '@editor/services/Points'
import { pollAsyncJobStatus } from '@editor/services/VolumeEstimation'
import { GenericError } from '@editor/services/errors/GenericError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { addSuccessTool, addWorkingTool, removeWorkingTool, setDisableTools } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import { MeshTile, getTileWorldBoundingBox } from '@infraloop/common'
import * as Sentry from '@sentry/react'
import { Vector3 } from 'three'
import { useThrottledCallback } from 'use-debounce'

import { usePolylineSelector } from '../../../store/selector'
import { MINIMUM_BOUNDARY_PADDING, boundaryThicknessFor } from '../../constants'
import { CrackDetectionStage, DetectionKind } from '../../interfaces'
import { PolylineToolMode, setMode } from '../../store/core'
import {
  ChalkDetectionResponse,
  CrackDetectionResponse,
  clearJobToken,
  detectCracks,
  resetCurrent,
  setChalkDetectionResult,
  setCrackDetectionResult,
  setDrawingStage,
} from '../../store/crackDetection'
import { addCompletedPolylines } from '../../store/polyline'
import {
  selectCrackDetectionJobToken,
  selectCrackDetectionStage,
  selectCrackDetectionWorkingPoints,
  selectDetectionKind,
  selectHasDetectionResult,
  selectPointForTargetColor,
} from '../../store/selectors'

const useEditor = (props: Editor): EditorConfig => {
  // Toast
  const toastIdRef = useRef<string>(undefined)

  // Context
  const { sceneRef, setPointProjectionPlane } = props
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const areaSwitched = useAppSelector((state) => state.page.areaSwitched)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)

  const executionId = usePolylineSelector((state) => state.crackDetection.executions[state.crackDetection.areaId]?.id)
  const hasDetectionResult = usePolylineSelector(selectHasDetectionResult)
  const drawingStage = usePolylineSelector(selectCrackDetectionStage)
  const workingPoints = usePolylineSelector(selectCrackDetectionWorkingPoints)
  const jobToken = usePolylineSelector(selectCrackDetectionJobToken)
  const detectionKind = usePolylineSelector(selectDetectionKind)
  const pointForTargetColor = usePolylineSelector(selectPointForTargetColor)

  // Derived state
  const isToolSelected = selectedTool === EDITOR_TOOLS.POLYLINE
  const isDirty = workingPoints.length > 0
  const isDetecting = drawingStage === CrackDetectionStage.Polling
  const isChalkDetection = detectionKind === DetectionKind.Chalk

  /** チョーキング検出 and クラック検出 share every message, so the noun is all that differs. */
  const detectionLabel = isChalkDetection ? 'チョーキング検出' : 'クラック検出'

  // Refs
  // Needs to be ref for the check function to get latest value
  const crackDetectionRunningRef = useRef(false)
  const currentAreaIdRef = useRef(inspectionArea?.inspection_area_id)
  const isToolSelectedRef = useRef(isToolSelected)
  const isDirtyRef = useRef(isDirty)
  const executionIdRef = useRef(executionId)
  const isChalkDetectionRef = useRef(isChalkDetection)

  // Execution whose chalk result has already been added. Chalk polylines go straight into the tool
  // and cannot be re-derived the way crack's point cloud can, so the polling effect re-firing must
  // not add them twice. Keyed on the run, not the mount: these hooks live for the whole editor
  // session, so a mount-scoped flag would swallow every detection after the first. Empty after a
  // reload, which is what lets a result the reload lost be picked up again.
  const chalkResultExecutionIdRef = useRef<string | undefined>(undefined)

  // Update refs
  useEffect(() => {
    isToolSelectedRef.current = isToolSelected
    currentAreaIdRef.current = inspectionArea?.inspection_area_id
    isDirtyRef.current = isDirty
    executionIdRef.current = executionId
    isChalkDetectionRef.current = isChalkDetection
  }, [isToolSelected, inspectionArea, isDirty, executionId, isChalkDetection])

  /**
   * Reset tool state
   */
  const resetTool = () => {
    dispatch(resetCurrent())
    setPointProjectionPlane(null)

    // `resetCurrent` leaves the run at `None`, but the tool is still in detection mode with
    // nothing drawn — and clicks only place points while the stage is `Drawing`. Without this the
    // canvas goes dead after a reset and the only way back is to cancel out and start the mode again.
    dispatch(setDrawingStage(CrackDetectionStage.Drawing))
  }

  /**
   * Handle submit button click
   */
  const handleSubmit = async () => {
    if (!project || !inspectionArea) return

    const token = await getAccessToken()
    if (!token) return

    const minBbox = new MinimumBoundingBox(
      workingPoints.map((pt) => new Vector3(pt[0], pt[1], pt[2])),
      boundaryThicknessFor(detectionKind),
      MINIMUM_BOUNDARY_PADDING,
    )
    const bounds = minBbox.getOBB3D()

    const meshFileNames: string[] = []
    const solidMeshManager = sceneRef.current?.getObjectByName('solid-mesh-manager')
    if (solidMeshManager) {
      solidMeshManager.traverse((tile) => {
        if (!(tile instanceof MeshTile) || !tile.objFilePath) return

        const bbox = getTileWorldBoundingBox(tile, solidMeshManager)
        if (bbox && minBbox.intersectsBox(bbox)) meshFileNames.push(tile.objFilePath)
      })
    }

    try {
      dispatch(setDisableTools(true))
      dispatch(setAttentionText({ message: `${detectionLabel}を開始しています。お待ちください...` }))

      // Initialize detection request. Sending the picked point is what makes this a chalk run.
      await dispatch(
        detectCracks({
          accessToken: token,
          projectId: project.project_id,
          areaId: inspectionArea.inspection_area_id,
          positions: workingPoints.map((pt) => ({ x: pt[0], y: pt[1], z: pt[2] })),
          bounds: {
            center: bounds.center.toArray(),
            rotation: bounds.rotation,
            extent: bounds.extent,
          },
          meshFileNames,
          pointForTargetColor:
            isChalkDetection && pointForTargetColor
              ? { x: pointForTargetColor[0], y: pointForTargetColor[1], z: pointForTargetColor[2] }
              : undefined,
        }),
      ).unwrap()

      // immediately switch over back to polyline mode
      dispatch(setMode(PolylineToolMode.Draw))

      if (toastIdRef.current) toaster.dismiss(String(toastIdRef.current))
      toastIdRef.current = toaster.create({
        title: `${detectionLabel}を開始しました`,
        description: `${detectionLabel}が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。`,
        type: 'info',
        duration: 5000,
        closable: true,
      })
    } catch (error) {
      Sentry.captureException(error)
      showErrorModal(
        error instanceof GenericError
          ? error.message
          : `${detectionLabel}中にエラーが発生しました。再試行してください。`,
      )
      dispatch(setDrawingStage(CrackDetectionStage.Failed))
      dispatch(clearJobToken())
    } finally {
      dispatch(setDisableTools(false))
    }
  }

  /**
   * Check for crack detection status.
   * It's throttled to avoid multiple calls during initial render.
   */
  const checkCrackDetection = useThrottledCallback(async () => {
    if (!jobToken || crackDetectionRunningRef.current) return

    const token = await getAccessToken()
    if (!token) return

    const areaId = currentAreaIdRef.current
    crackDetectionRunningRef.current = true
    dispatch(addWorkingTool(EDITOR_TOOLS.POLYLINE))

    try {
      // The detection runs async in the back-end. Poll for results using the job token.
      const response = await pollAsyncJobStatus<CrackDetectionResponse | ChalkDetectionResponse>(token, jobToken)
      if (!response) throw new Error('crack detection failed')

      // The area has changed, ignore the result
      // Or if we're no longer dirty (user has reset), ignore as well
      if (areaId !== currentAreaIdRef.current || !isDirtyRef.current) return

      // Chalk detection hands back the polylines themselves, so they go straight into the tool —
      // there is no point cloud to download and no client-side extraction to run.
      if (isChalkDetectionRef.current && 'polylines' in response) {
        const resultExecutionId = executionIdRef.current
        if (!resultExecutionId || chalkResultExecutionIdRef.current === resultExecutionId) return
        chalkResultExecutionIdRef.current = resultExecutionId

        dispatch(
          addCompletedPolylines(
            response.polylines.map(({ registration_result, parameters_polyline }) => {
              const vertices = parameters_polyline.vertices.map((vertex) => new Vector3(...vertex))

              return {
                id: crypto.randomUUID(),
                points: registration_result
                  ? transform2Dto3D(vertices, Object.values(registration_result.transformation))
                  : parameters_polyline.vertices,
                jobId: resultExecutionId,
              }
            }),
          ),
        )
        dispatch(setChalkDetectionResult())
        dispatch(setMode(PolylineToolMode.Draw))
      }

      // TODO: SUPER WEIRD need to check object since the return type of pollAsyncJobStatus consist of boolean
      //       Need to figure out a cleaner way to deal with this later.
      if (response && typeof response === 'object' && 'url_pcd' in response && 'path_entwine' in response) {
        dispatch(
          setCrackDetectionResult({
            pcdURL: response.url_pcd,
            pathEntwine: response.path_entwine,
          }),
        )
      }

      if (toastIdRef.current) toaster.dismiss(String(toastIdRef.current))

      const completionTitle = `${isChalkDetectionRef.current ? 'チョーキング検出' : 'クラック検出'}が完了しました`

      // Different message depending on whether user is still in Polyline tool or not
      if (isToolSelectedRef.current) {
        toastIdRef.current = toaster.create({
          title: completionTitle,
          description: `${completionTitle}。結果は描画されたエリア内に表示されます。`,
          type: 'success',
          duration: 5000,
          closable: true,
        })
      } else {
        toastIdRef.current = toaster.create({
          title: completionTitle,
          description: `${completionTitle}。延長計算ツールに切り替えるか、下のボタンをクリックして結果を確認できます。`,
          type: 'success',
          duration: Infinity,
          closable: true,
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      showErrorModal(
        error instanceof GenericError
          ? error.message
          : `${isChalkDetectionRef.current ? 'チョーキング検出' : 'クラック検出'}中にエラーが発生しました。再試行してください。`,
      )
      dispatch(setDrawingStage(CrackDetectionStage.Failed))
      dispatch(clearJobToken())
    } finally {
      crackDetectionRunningRef.current = false
      dispatch(removeWorkingTool(EDITOR_TOOLS.POLYLINE))

      // only add if we're not in polyline tool
      if (!isToolSelectedRef.current && areaId === currentAreaIdRef.current)
        dispatch(addSuccessTool(EDITOR_TOOLS.POLYLINE))
    }
  }, 1000)

  /**
   * Check for crack detection status once job token is set.
   */
  useEffect(() => {
    if (!project || !inspectionArea || !jobToken) return

    void checkCrackDetection()
  }, [checkCrackDetection, inspectionArea, jobToken, project])

  /**
   * Close toast when area is switched.
   */
  useEffect(() => {
    if (areaSwitched && toastIdRef.current) {
      toaster.dismiss(String(toastIdRef.current))
      toastIdRef.current = undefined
    }
  }, [toastIdRef, areaSwitched])

  /**
   * Close toast when crack detection is cleared but toast is still open.
   */
  useEffect(() => {
    if (drawingStage === CrackDetectionStage.None && toastIdRef.current) {
      toaster.dismiss(String(toastIdRef.current))
      toastIdRef.current = undefined
    }
  }, [drawingStage, toastIdRef])

  /**
   * Clear job token when tool is selected again.
   */
  useEffect(() => {
    if (isToolSelected) {
      if (hasDetectionResult) dispatch(clearJobToken())
    }
  }, [isToolSelected, hasDetectionResult, dispatch])

  /**
   * Close toast once tool is selected again.
   */
  useEffect(() => {
    // Close any existing toasts
    if (isToolSelected) {
      if (toastIdRef.current) toaster.dismiss(String(toastIdRef.current))
    }
  }, [isToolSelected, dispatch])

  return {
    buttons: {
      submit: {
        label: '検出開始',
        loadingLabel: '検出中...',
        isShown: () => isToolSelected,
        isDisabled: () =>
          drawingStage !== CrackDetectionStage.Preview ||
          isDetecting ||
          // A chalk run cannot start until the user has picked the colour to look for.
          (isChalkDetection && !pointForTargetColor),
        isLoading: () => isDetecting,
        onClick: handleSubmit,
      },
      reset: {
        onClick: resetTool,
        isShown: () => isToolSelected,
        isDisabled: () => !isDirty || isDetecting,
      },
      undo: {
        isShown: () => isToolSelected,
        isDisabled: () => !isDirty || isDetecting,
      },
    },
  }
}

export default useEditor
